import { csvComoObjetos, lerData, paraCsv, validarAnimal, type AnimalCadastro, type Problema } from '@equiview/domain';
import { uuidv7 } from '@equiview/sync';
import type { Animal, AnimalEntrada } from './fonte.js';

/** Colunas aceitas (cabecalho normalizado: sem acento, minusculo, com _). */
const COLUNAS: Record<keyof LinhaCrua, readonly string[]> = {
  nome: ['nome', 'animal', 'nome_do_animal'],
  registro: ['registro', 'registro_abccmm', 'abccmm'],
  sexo: ['sexo'],
  nascimento: ['nascimento', 'data_de_nascimento', 'data_nascimento'],
  pelagem: ['pelagem', 'cor', 'pelagem_cor'],
  marcha: ['marcha', 'tipo_de_marcha', 'marcha_declarada'],
  pai: ['pai', 'registro_do_pai', 'pai_registro'],
  mae: ['mae', 'registro_da_mae', 'mae_registro'],
  microchip: ['microchip', 'chip'],
  observacoes: ['observacoes', 'obs', 'notas'],
};

interface LinhaCrua {
  nome: string;
  registro: string;
  sexo: string;
  nascimento: string;
  pelagem: string;
  marcha: string;
  pai: string;
  mae: string;
  microchip: string;
  observacoes: string;
}

export interface LinhaImportada {
  /** Linha na planilha (a 1a e o cabecalho). */
  linha: number;
  nome: string;
  situacao: 'nova' | 'existe' | 'erro';
  animal: AnimalEntrada & { id: string };
  erros: string[];
  avisos: string[];
}

const SEXOS: Record<string, Animal['sex']> = {
  macho: 'macho_inteiro',
  macho_inteiro: 'macho_inteiro',
  garanhao: 'macho_inteiro',
  inteiro: 'macho_inteiro',
  castrado: 'castrado',
  femea: 'femea',
  egua: 'femea',
};

const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
const registroNormal = (r: string) => r.trim().toUpperCase().replace(/\s+/g, '');

/** Modelo para baixar: as colunas que a importacao entende, com uma linha de exemplo. */
export const modeloDePlanilha = () =>
  paraCsv(
    Object.keys(COLUNAS).map((c) => ({ chave: c, titulo: c })),
    [
      {
        nome: 'Estrela do Recanto',
        registro: 'MM-104217',
        sexo: 'fêmea',
        nascimento: '15/03/2019',
        pelagem: 'Tordilha',
        marcha: 'batida',
        pai: 'MM-099001',
        mae: '',
        microchip: '',
        observacoes: '',
      },
    ],
  );

/**
 * Da planilha ao cadastro: cada linha vira um animal do haras, validado com a
 * MESMA regra do formulario (validarAnimal). Pai e mae sao achados pelo registro,
 * entre os animais ja cadastrados ou na propria planilha. Registro que ja existe
 * no aparelho nao e importado de novo.
 */
export function prepararImportacao(
  texto: string,
  { existentes, harasId, hoje }: { existentes: readonly Animal[]; harasId: string; hoje: string },
): LinhaImportada[] {
  const objetos = csvComoObjetos(texto);
  const pegar = (o: Record<string, string>, campo: keyof LinhaCrua) => COLUNAS[campo].map((c) => o[c]).find((v) => v) ?? '';
  const cruas = objetos.map((o) =>
    Object.fromEntries((Object.keys(COLUNAS) as (keyof LinhaCrua)[]).map((c) => [c, pegar(o, c)])) as unknown as LinhaCrua,
  );

  const porRegistro = new Map(existentes.filter((a) => a.abccmm_registry).map((a) => [registroNormal(a.abccmm_registry!), a.id]));
  const novosPorRegistro = new Map<string, string>();
  const repetidosNaPlanilha = new Set<string>();
  const ids = cruas.map((c) => {
    const reg = registroNormal(c.registro);
    const id = uuidv7();
    if (reg) {
      if (novosPorRegistro.has(reg)) repetidosNaPlanilha.add(reg);
      else novosPorRegistro.set(reg, id);
    }
    return id;
  });
  const acharPai = (reg: string) => (reg ? (porRegistro.get(registroNormal(reg)) ?? novosPorRegistro.get(registroNormal(reg)) ?? null) : null);

  const linhas = cruas.map((c, i): LinhaImportada => {
    const erros: string[] = [];
    const reg = registroNormal(c.registro);
    const sexo = SEXOS[semAcento(c.sexo)];
    const nascimento = lerData(c.nascimento);
    const marcha = semAcento(c.marcha);
    if (!c.nome) erros.push('Falta o nome.');
    if (!sexo) erros.push(c.sexo ? `Sexo "${c.sexo}" não reconhecido (use macho, castrado ou fêmea).` : 'Falta o sexo.');
    if (!nascimento) erros.push(c.nascimento ? `Data "${c.nascimento}" inválida (use dd/mm/aaaa).` : 'Falta o nascimento.');
    if (reg && repetidosNaPlanilha.has(reg)) erros.push(`Registro ${reg} aparece mais de uma vez na planilha.`);
    const sire = acharPai(c.pai);
    const dam = acharPai(c.mae);
    if (c.pai && !sire) erros.push(`Pai ${c.pai} não encontrado: cadastre o pai antes ou inclua-o na planilha.`);
    if (c.mae && !dam) erros.push(`Mãe ${c.mae} não encontrada: cadastre a mãe antes ou inclua-a na planilha.`);

    const animal: AnimalEntrada & { id: string } = {
      id: ids[i]!,
      name: c.nome,
      abccmm_registry: reg || null,
      sex: sexo ?? 'femea',
      birth_date: nascimento ?? '',
      birth_date_estimated: false,
      stud_book: null,
      microchip: c.microchip || null,
      coat_color: c.pelagem || null,
      marcha_type_declared: marcha === 'batida' || marcha === 'picada' ? marcha : null,
      owner_farm_id: harasId,
      // Como no formulario: sem informacao, o criador e o proprio haras.
      breeder_farm_id: harasId,
      sire_id: sire,
      dam_id: dam,
      notes: c.observacoes || null,
    };
    const existe = !!reg && porRegistro.has(reg);
    return { linha: i + 2, nome: c.nome, situacao: existe ? 'existe' : erros.length ? 'erro' : 'nova', animal, erros, avisos: [] };
  });

  // A regra do formulario, com todos (ja cadastrados + os novos) para genealogia e registro.
  const todos: AnimalCadastro[] = [
    ...existentes.map((a) => ({ ...a })),
    ...linhas.filter((l) => l.situacao === 'nova').map((l) => ({ ...l.animal })),
  ];
  for (const l of linhas) {
    if (l.situacao !== 'nova') continue;
    const v = validarAnimal(l.animal, { todos, hoje });
    const texto = (p: Problema) => p.mensagem;
    l.erros.push(...v.erros.map(texto));
    l.avisos.push(...v.avisos.map(texto));
    if (l.erros.length) l.situacao = 'erro';
  }
  return linhas;
}

/** Ordem de gravacao: pais antes dos filhos, porque o banco confere a genealogia. */
export function ordemDeGravacao(linhas: readonly LinhaImportada[]): LinhaImportada[] {
  const novas = linhas.filter((l) => l.situacao === 'nova');
  const ids = new Set(novas.map((l) => l.animal.id));
  const feitas = new Set<string>();
  const ordem: LinhaImportada[] = [];
  while (ordem.length < novas.length) {
    const antes = ordem.length;
    for (const l of novas) {
      if (feitas.has(l.animal.id)) continue;
      const pais = [l.animal.sire_id, l.animal.dam_id].filter((p): p is string => !!p && ids.has(p));
      if (pais.every((p) => feitas.has(p))) {
        ordem.push(l);
        feitas.add(l.animal.id);
      }
    }
    if (ordem.length === antes) break; // ciclo: a validacao ja apontou
  }
  return ordem;
}
